// src/stock/Tracabilite.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Row, Col, Button, Spinner, Form, Table,
    Nav, Tab, OverlayTrigger, Tooltip, InputGroup
} from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatDate, formatDateTime, formatCurrency } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function Tracabilite() {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [historique, setHistorique] = useState(null);
    const [stats, setStats] = useState(null);
    const [lots, setLots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('chrono');
    const [filtres, setFiltres] = useState({ date_debut: '', date_fin: '', type: '' });
    const [showFilters, setShowFilters] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        if (selectedProduct) {
            loadHistorique(selectedProduct.id_produit);
            loadStats(selectedProduct.id_produit);
            loadLots(selectedProduct.id_produit);
        }
    }, [filtres]);

    /* ============================================================
       FETCH
       ============================================================ */
    const handleSearch = async () => {
        if (!searchTerm.trim()) return;
        setSearching(true);
        setError(null);
        setSearchPerformed(false);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/tracabilite.php?action=rechercher&q=${encodeURIComponent(searchTerm)}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur recherche");
            const result = await response.json();
            setSearchResults(result.data || []);
            setSearchPerformed(true);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
            setSearchPerformed(true);
        } finally {
            setSearching(false);
        }
    };

    const handleSelectProduct = async (produit) => {
        setSelectedProduct(produit);
        setSearchResults([]);
        setSearchTerm('');
        setSearchPerformed(false);
        setLoading(true);
        try {
            await loadHistorique(produit.id_produit);
            await loadStats(produit.id_produit);
            await loadLots(produit.id_produit);
        } catch (err) {
            showToast('error', 'Erreur', "Erreur lors du chargement des données");
        } finally {
            setLoading(false);
        }
    };

    const loadHistorique = async (id_produit) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/tracabilite.php?action=historique&id_produit=${id_produit}`;
            const params = new URLSearchParams();
            if (filtres.date_debut) params.append('date_debut', filtres.date_debut);
            if (filtres.date_fin) params.append('date_fin', filtres.date_fin);
            if (filtres.type) params.append('type', filtres.type);
            if (params.toString()) url += '&' + params.toString();
            const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setHistorique(result.data);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async (id_produit) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/tracabilite.php?action=stats&id_produit=${id_produit}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const loadLots = async (id_produit) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/tracabilite.php?action=lots&id_produit=${id_produit}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setLots(result.data || []);
            }
        } catch (err) {
            console.error("Erreur lots", err);
        }
    };

    const resetFilters = () => {
        setFiltres({ date_debut: '', date_fin: '', type: '' });
        if (selectedProduct) loadHistorique(selectedProduct.id_produit);
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getTypeBadge = (type) => {
        const configs = {
            entree:              { gradient: 'linear-gradient(135deg, #10b981, #059669)', label: 'Entrée',             icon: 'ri-add-line' },
            sortie:              { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', label: 'Sortie',             icon: 'ri-subtract-line' },
            inventaire:          { gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)', label: 'Inventaire',          icon: 'ri-file-list-line' },
            commande_fournisseur:{ gradient: 'linear-gradient(135deg, #295AA7, #1e3f7a)', label: 'Commande fournisseur', icon: 'ri-truck-line' },
            facture:             { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', label: 'Facture',             icon: 'ri-file-text-line' },
            bon_livraison:       { gradient: 'linear-gradient(135deg, #64748b, #475569)', label: 'Bon de livraison',    icon: 'ri-truck-line' }
        };
        const config = configs[type] || { gradient: 'linear-gradient(135deg, #64748b, #475569)', label: type, icon: 'ri-file-line' };
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    const getChronologie = () => {
        if (!historique) return [];
        const evenements = [];
        historique.mouvements?.forEach(m => {
            evenements.push({
                date: m.date_mouvement, type: 'mouvement', sous_type: m.type_mouvement,
                titre: `${m.type_mouvement === 'entree' ? '➕' : '➖'} Mouvement de stock`,
                description: `${m.type_mouvement === 'entree' ? 'Entrée' : 'Sortie'} de ${m.quantite} unités`,
                document: m.reference_document, document_type: m.type_document,
                quantite: m.quantite, stock_apres: m.stock_apres, utilisateur: m.utilisateur,
                couleur: m.type_mouvement === 'entree' ? COLORS.success : COLORS.danger,
                icone: m.type_mouvement === 'entree' ? 'ri-arrow-down-line' : 'ri-arrow-up-line',
                id: m.id_mouvement
            });
        });
        historique.commandes?.forEach(c => {
            evenements.push({
                date: c.date_commande, type: 'document', sous_type: 'commande_fournisseur',
                titre: 'Commande fournisseur', description: `Commande chez ${c.tiers_nom}`,
                document: c.numero_commande, document_type: 'commande_fournisseur',
                quantite: c.quantite, tiers: c.tiers_nom,
                couleur: COLORS.primary, icone: 'ri-truck-line', id: c.id_commande_fournisseur
            });
        });
        historique.factures?.forEach(f => {
            evenements.push({
                date: f.date_facture, type: 'document', sous_type: 'facture',
                titre: 'Facture client', description: `Facture pour ${f.tiers_nom}`,
                document: f.numero_facture, document_type: 'facture',
                quantite: f.quantite, tiers: f.tiers_nom,
                couleur: COLORS.warning, icone: 'ri-file-list-line', id: f.id_facture
            });
        });
        historique.bons_livraison?.forEach(b => {
            evenements.push({
                date: b.date_document, type: 'document', sous_type: 'bon_livraison',
                titre: 'Bon de livraison', description: `Livraison pour ${b.tiers_nom}`,
                document: b.numero_bl, document_type: 'bon_livraison',
                quantite: b.quantite, tiers: b.tiers_nom, lot: b.lot, date_peremption: b.date_peremption,
                couleur: '#64748b', icone: 'ri-truck-line', id: b.id_bon_livraison
            });
        });
        historique.inventaires?.forEach(i => {
            evenements.push({
                date: i.date_inventaire, type: 'inventaire', sous_type: 'inventaire',
                titre: 'Inventaire', description: `Écart: ${i.ecart > 0 ? '+' : ''}${i.ecart}`,
                document: i.reference_inventaire, document_type: 'inventaire',
                quantite_theorique: i.quantite_theorique, quantite_reelle: i.quantite_reelle, ecart: i.ecart,
                utilisateur: i.utilisateur,
                couleur: i.ecart === 0 ? COLORS.success : i.ecart > 0 ? COLORS.info : COLORS.danger,
                icone: 'ri-survey-line', id: i.id_inventaire
            });
        });
        return evenements.sort((a, b) => new Date(b.date) - new Date(a.date));
    };

    const chronologie = getChronologie();

    /* ============================================================
       RENDU
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-search-eye-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Traçabilité</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-information-line"></i>
                                        Cycle de vie complet des produits
                                    </span>
                                    {selectedProduct && (
                                        <span className="badge-modern" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                            <i className="ri-barcode-line"></i> {selectedProduct.reference}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            {selectedProduct && (
                                <Button className="btn-modern-outline" onClick={() => setSelectedProduct(null)}>
                                    <i className="ri-close-line"></i> Changer de produit
                                </Button>
                            )}
                            <Button className="btn-modern-outline" onClick={() => navigate('/stock/etat/instantane')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                        </div>
                    </div>

                    {/* ===== BARRE DE RECHERCHE ===== */}
                    {!selectedProduct && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4">
                            <Card.Body className="p-4">
                                <div className="section-title-charte">
                                    <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                        <i className="ri-search-line"></i>
                                    </span>
                                    <h6>Rechercher un produit</h6>
                                </div>
                                <Row className="g-3 align-items-center">
                                    <Col md="10">
                                        <InputGroup className="input-modern">
                                            <InputGroup.Text><i className="ri-search-line"></i></InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="Référence, désignation ou code-barres..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                            />
                                        </InputGroup>
                                    </Col>
                                    <Col md="2">
                                        <Button className="btn-modern-primary w-100 justify-content-center" onClick={handleSearch} disabled={searching}>
                                            {searching ? <Spinner size="sm" /> : <><i className="ri-search-line me-1"></i>Chercher</>}
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}

                    {/* ===== RÉSULTATS DE RECHERCHE ===== */}
                    {searchPerformed && !selectedProduct && (
                        <div className="mb-4">
                            {searchResults.length > 0 ? (
                                <>
                                    <div className="section-title-charte mb-3">
                                        <span className="section-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                            <i className="ri-check-line"></i>
                                        </span>
                                        <h6>{searchResults.length} résultat{searchResults.length > 1 ? 's' : ''} trouvé{searchResults.length > 1 ? 's' : ''}</h6>
                                    </div>
                                    <Row className="g-4">
                                        {searchResults.map(p => (
                                            <Col md={6} lg={4} key={p.id_produit}>
                                                <div className="product-card-charte" onClick={() => handleSelectProduct(p)}>
                                                    <div className="d-flex align-items-start gap-3 mb-3">
                                                        <div className="product-avatar-charte"><i className="ri-barcode-line"></i></div>
                                                        <div className="flex-grow-1">
                                                            <h6 className="mb-1 fw-bold">{p.designation}</h6>
                                                            <div className="d-flex flex-wrap gap-2 mt-1">
                                                                <span className="reference-badge-charte">{p.reference}</span>
                                                                {p.nom_categorie && (
                                                                    <span className="category-badge-charte">{p.nom_categorie}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                                                            <i className="ri-stack-line"></i> {p.stock_actuel || 0}
                                                        </span>
                                                    </div>
                                                    <div className="d-flex justify-content-between text-muted small flex-wrap gap-2">
                                                        <span><i className="ri-money-euro-circle-line me-1"></i>{formatCurrency(p.prix_achat_ht)}</span>
                                                        <span><i className="ri-map-pin-line me-1"></i>{p.emplacement || 'Sans emplacement'}</span>
                                                        <span><i className="ri-truck-line me-1"></i>{p.fournisseur_nom || '—'}</span>
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </>
                            ) : (
                                <div className="empty-state-charte">
                                    <div className="empty-state-icon-charte"><i className="ri-search-line"></i></div>
                                    <h6 className="fw-semibold mb-1">Aucun produit trouvé</h6>
                                    <p className="text-muted small mb-3">Aucun produit ne correspond à votre recherche</p>
                                    <Button className="btn-modern-outline" onClick={() => { setSearchTerm(''); setSearchPerformed(false); }}>
                                        <i className="ri-refresh-line me-1"></i>Nouvelle recherche
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ===== VUE DÉTAILLÉE PRODUIT ===== */}
                    {selectedProduct && (
                        <>
                            {/* Carte produit */}
                            <Card className="border-0 shadow-sm rounded-3 mb-4 overflow-hidden">
                                <div className="product-header-charte">
                                    <div className="product-avatar-charte-large">
                                        <i className="ri-barcode-line"></i>
                                    </div>
                                    <div className="flex-grow-1">
                                        <h4 className="fw-bold text-white mb-2">{selectedProduct.designation}</h4>
                                        <div className="d-flex flex-wrap gap-3">
                                            <span className="text-white-50 small"><i className="ri-barcode-line me-1"></i>{selectedProduct.reference}</span>
                                            {selectedProduct.nom_categorie && (
                                                <span className="text-white-50 small"><i className="ri-folder-line me-1"></i>{selectedProduct.nom_categorie}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-white text-end">
                                        <div className="mb-2">
                                            <div className="text-white-50 small">Stock actuel</div>
                                            <div className="fs-3 fw-bold">{selectedProduct.stock_actuel || 0}</div>
                                        </div>
                                        <div>
                                            <div className="text-white-50 small">Valeur</div>
                                            <div className="fs-5 fw-semibold">
                                                {formatCurrency((selectedProduct.stock_actuel || 0) * selectedProduct.prix_achat_ht)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card>

                            {/* KPI */}
                            {stats && (
                                <Row className="g-3 mb-4">
                                    <Col sm="6" xl="3">
                                        <div className="kpi-charte kpi-primary">
                                            <div className="kpi-icon-charte"><i className="ri-stack-line"></i></div>
                                            <div className="kpi-content-charte">
                                                <span className="kpi-label-charte">Total mouvements</span>
                                                <h3 className="kpi-value-charte">{stats.total?.total_mouvements || 0}</h3>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col sm="6" xl="3">
                                        <div className="kpi-charte kpi-success">
                                            <div className="kpi-icon-charte"><i className="ri-arrow-down-line"></i></div>
                                            <div className="kpi-content-charte">
                                                <span className="kpi-label-charte">Entrées</span>
                                                <h3 className="kpi-value-charte">{stats.total?.entrees || 0}</h3>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col sm="6" xl="3">
                                        <div className="kpi-charte kpi-danger">
                                            <div className="kpi-icon-charte"><i className="ri-arrow-up-line"></i></div>
                                            <div className="kpi-content-charte">
                                                <span className="kpi-label-charte">Sorties</span>
                                                <h3 className="kpi-value-charte">{stats.total?.sorties || 0}</h3>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col sm="6" xl="3">
                                        <div className="kpi-charte kpi-info">
                                            <div className="kpi-icon-charte"><i className="ri-calendar-line"></i></div>
                                            <div className="kpi-content-charte">
                                                <span className="kpi-label-charte">Premier mouvement</span>
                                                <h3 className="kpi-value-charte" style={{ fontSize: '0.9rem' }}>
                                                    {stats.premier_mouvement?.premier_mouvement
                                                        ? formatDate(stats.premier_mouvement.premier_mouvement)
                                                        : '—'}
                                                </h3>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            {/* Filtres */}
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Body className="p-4">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="section-title-charte mb-0">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                <i className="ri-filter-line"></i>
                                            </span>
                                            <h6>Filtres</h6>
                                        </div>
                                        <Button variant="link" className="text-muted p-0 text-decoration-none btn-toggle-filters-charte" onClick={() => setShowFilters(!showFilters)}>
                                            <i className={`ri-arrow-${showFilters ? 'up' : 'down'}-s-line me-1`}></i>
                                            {showFilters ? 'Masquer' : 'Afficher'}
                                        </Button>
                                    </div>

                                    {showFilters && (
                                        <Row className="g-3 mt-3">
                                            <Col md="3">
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Date début</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-calendar-line"></i></InputGroup.Text>
                                                        <Form.Control type="date" value={filtres.date_debut}
                                                            onChange={(e) => setFiltres({ ...filtres, date_debut: e.target.value })} />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md="3">
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Date fin</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-calendar-line"></i></InputGroup.Text>
                                                        <Form.Control type="date" value={filtres.date_fin}
                                                            onChange={(e) => setFiltres({ ...filtres, date_fin: e.target.value })} />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md="3">
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Type</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-filter-line"></i></InputGroup.Text>
                                                        <Form.Select value={filtres.type}
                                                            onChange={(e) => setFiltres({ ...filtres, type: e.target.value })}>
                                                            <option value="">Tous</option>
                                                            <option value="entree">Entrées</option>
                                                            <option value="sortie">Sorties</option>
                                                            <option value="inventaire">Inventaires</option>
                                                        </Form.Select>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md="3" className="d-flex align-items-end gap-2">
                                                <Button className="btn-modern-primary flex-grow-1 justify-content-center" onClick={() => loadHistorique(selectedProduct.id_produit)}>
                                                    <i className="ri-filter-line me-1"></i>Appliquer
                                                </Button>
                                                <Button className="btn-modern-outline" onClick={resetFilters}>
                                                    <i className="ri-refresh-line"></i>
                                                </Button>
                                            </Col>
                                        </Row>
                                    )}
                                </Card.Body>
                            </Card>

                            {/* Onglets traçabilité */}
                            <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-search-eye-line text-white fs-5"></i>
                                        <span className="fw-semibold text-white">Traçabilité du produit</span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <div className="px-4 pt-4">
                                        <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="tracabilite-tabs-charte">
                                            <Nav.Item>
                                                <Nav.Link eventKey="chrono"><i className="ri-timeline-line me-2"></i>Chronologie</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="mouvements"><i className="ri-stack-line me-2"></i>Mouvements</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="documents"><i className="ri-file-list-line me-2"></i>Documents</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="lots">
                                                    <i className="ri-barcode-line me-2"></i>Lots & Séries
                                                    {lots.length > 0 && (
                                                        <span className="badge-tab-count-charte">{lots.length}</span>
                                                    )}
                                                </Nav.Link>
                                            </Nav.Item>
                                        </Nav>
                                    </div>
                                    <Tab.Content className="p-4">

                                        {/* Chronologie */}
                                        <Tab.Pane active={activeTab === 'chrono'}>
                                            {loading ? (
                                                <div className="text-center py-5">
                                                    <Spinner animation="border" style={{ color: COLORS.primary }} />
                                                </div>
                                            ) : chronologie.length > 0 ? (
                                                <div className="chronology-list-charte">
                                                    {chronologie.map((event, index) => (
                                                        <div key={index} className="chronology-item-charte">
                                                            <div className="chronology-icon-charte" style={{ background: event.couleur }}>
                                                                <i className={`${event.icone} text-white`}></i>
                                                            </div>
                                                            <div className="chronology-content-charte">
                                                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                                                    <div>
                                                                        <h6 className="mb-1">{event.titre}</h6>
                                                                        <div className="d-flex gap-2 align-items-center flex-wrap">
                                                                            {getTypeBadge(event.sous_type || event.type)}
                                                                            <small className="text-muted">
                                                                                <i className="ri-time-line me-1"></i>
                                                                                {formatDateTime(event.date)}
                                                                            </small>
                                                                        </div>
                                                                    </div>
                                                                    {event.document && (
                                                                        <span className="reference-badge-charte">{event.document}</span>
                                                                    )}
                                                                </div>
                                                                <p className="text-muted mt-2 mb-0 small">{event.description}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="empty-state-charte">
                                                    <div className="empty-state-icon-charte"><i className="ri-history-line"></i></div>
                                                    <h6 className="fw-semibold mb-1">Aucun événement trouvé</h6>
                                                </div>
                                            )}
                                        </Tab.Pane>

                                        {/* Mouvements */}
                                        <Tab.Pane active={activeTab === 'mouvements'}>
                                            {loading ? (
                                                <div className="text-center py-5">
                                                    <Spinner animation="border" style={{ color: COLORS.primary }} />
                                                </div>
                                            ) : historique?.mouvements?.length > 0 ? (
                                                <div className="table-responsive">
                                                    <Table hover className="align-middle mb-0 table-charte">
                                                        <thead>
                                                            <tr>
                                                                <th className="ps-4">Date</th>
                                                                <th className="text-center">Type</th>
                                                                <th className="text-end">Quantité</th>
                                                                <th className="text-center">Stock après</th>
                                                                <th>Référence</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {historique.mouvements.map((m) => (
                                                                <tr key={m.id_mouvement}>
                                                                    <td className="ps-4 text-muted">
                                                                        <i className="ri-time-line me-1"></i>{formatDateTime(m.date_mouvement)}
                                                                    </td>
                                                                    <td className="text-center">{getTypeBadge(m.type_mouvement)}</td>
                                                                    <td className="text-end fw-bold">
                                                                        <span style={{ color: m.type_mouvement === 'entree' ? COLORS.success : COLORS.danger }}>
                                                                            {m.type_mouvement === 'entree' ? '+' : '−'}{m.quantite}
                                                                        </span>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <span className="quantity-badge-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>{m.stock_apres}</span>
                                                                    </td>
                                                                    <td><small className="text-muted">{m.reference_document || '—'}</small></td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            ) : (
                                                <div className="empty-state-charte">
                                                    <div className="empty-state-icon-charte"><i className="ri-stack-line"></i></div>
                                                    <h6 className="fw-semibold mb-1">Aucun mouvement trouvé</h6>
                                                </div>
                                            )}
                                        </Tab.Pane>

                                        {/* Documents */}
                                        <Tab.Pane active={activeTab === 'documents'}>
                                            {loading ? (
                                                <div className="text-center py-5">
                                                    <Spinner animation="border" style={{ color: COLORS.primary }} />
                                                </div>
                                            ) : (
                                                <>
                                                    {historique?.commandes?.length > 0 && (
                                                        <div className="mb-4">
                                                            <div className="section-title-charte">
                                                                <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                                    <i className="ri-truck-line"></i>
                                                                </span>
                                                                <h6>Commandes fournisseurs</h6>
                                                            </div>
                                                            <div className="table-responsive">
                                                                <Table hover className="align-middle mb-0 table-charte">
                                                                    <thead>
                                                                        <tr>
                                                                            <th className="ps-4">N° Commande</th>
                                                                            <th>Date</th>
                                                                            <th>Fournisseur</th>
                                                                            <th className="text-end">Quantité</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {historique.commandes.map((c, i) => (
                                                                            <tr key={i}>
                                                                                <td className="ps-4">
                                                                                    <Link to={`/achats/commandes/${c.id_commande_fournisseur}`} className="table-link-charte">
                                                                                        {c.numero_commande}
                                                                                    </Link>
                                                                                </td>
                                                                                <td>{formatDate(c.date_commande)}</td>
                                                                                <td>{c.tiers_nom}</td>
                                                                                <td className="text-end fw-bold" style={{ color: COLORS.success }}>{c.quantite}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {(!historique?.commandes || historique.commandes.length === 0) && (
                                                        <div className="empty-state-charte">
                                                            <div className="empty-state-icon-charte"><i className="ri-file-list-line"></i></div>
                                                            <h6 className="fw-semibold mb-1">Aucun document trouvé</h6>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </Tab.Pane>

                                        {/* Lots */}
                                        <Tab.Pane active={activeTab === 'lots'}>
                                            {lots.length > 0 ? (
                                                <Row className="g-4">
                                                    {lots.map((lot, index) => {
                                                        const estPerime = lot.date_peremption && new Date(lot.date_peremption) < new Date();
                                                        const joursRestant = lot.date_peremption
                                                            ? Math.ceil((new Date(lot.date_peremption) - new Date()) / (1000 * 60 * 60 * 24))
                                                            : null;
                                                        return (
                                                            <Col md={6} key={index}>
                                                                <Card className="border-0 shadow-sm rounded-3 h-100 lot-card-charte">
                                                                    <Card.Body className="p-4">
                                                                        <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                                                                            <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                                                                                <i className="ri-barcode-line"></i> Lot: {lot.lot}
                                                                            </span>
                                                                            <span className="category-badge-charte">
                                                                                {lot.nb_lignes} mouvement(s)
                                                                            </span>
                                                                        </div>
                                                                        <div className="info-row-charte">
                                                                            <span className="info-label-charte">Quantité totale</span>
                                                                            <span className="fw-bold" style={{ color: COLORS.primary }}>{lot.quantite_totale}</span>
                                                                        </div>
                                                                        {lot.date_peremption && (
                                                                            <>
                                                                                <div className="info-row-charte">
                                                                                    <span className="info-label-charte">Date péremption</span>
                                                                                    <span className={`fw-bold ${estPerime ? 'text-danger' : ''}`}>
                                                                                        {formatDate(lot.date_peremption)}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="info-row-charte">
                                                                                    <span className="info-label-charte">Statut</span>
                                                                                    {estPerime ? (
                                                                                        <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
                                                                                            <i className="ri-close-circle-line"></i>Périmé
                                                                                        </span>
                                                                                    ) : joursRestant && joursRestant <= 30 ? (
                                                                                        <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                                                                                            <i className="ri-time-line"></i>Expire dans {joursRestant}j
                                                                                        </span>
                                                                                    ) : (
                                                                                        <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                                                                                            <i className="ri-check-line"></i>Valide
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </Card.Body>
                                                                </Card>
                                                            </Col>
                                                        );
                                                    })}
                                                </Row>
                                            ) : (
                                                <div className="empty-state-charte">
                                                    <div className="empty-state-icon-charte"><i className="ri-barcode-line"></i></div>
                                                    <h6 className="fw-semibold mb-1">Aucun lot ou numéro de série</h6>
                                                    <p className="text-muted small mb-0">Ce produit n'a pas de traçabilité par lot</p>
                                                </div>
                                            )}
                                        </Tab.Pane>
                                    </Tab.Content>
                                </Card.Body>
                            </Card>
                        </>
                    )}
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }

                /* ===== SECTION ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== FORM ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control,
                .input-modern .form-select {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus,
                .input-modern .form-select:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }

                /* ===== PRODUCT CARD ===== */
                .product-card-charte {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 16px;
                    padding: 1.25rem;
                    transition: all 0.25s ease;
                    cursor: pointer;
                    height: 100%;
                }
                .product-card-charte:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                    border-color: var(--primary);
                }
                .product-avatar-charte {
                    width: 48px; height: 48px;
                    background: #f1f5f9;
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    color: var(--primary); font-size: 1.3rem;
                    flex-shrink: 0;
                }
                .product-avatar-charte-large {
                    width: 64px; height: 64px;
                    background: rgba(255,255,255,0.15);
                    border-radius: 16px;
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-size: 1.8rem;
                    flex-shrink: 0;
                }
                .product-header-charte {
                    display: flex;
                    align-items: center;
                    gap: 1.5rem;
                    padding: 1.5rem;
                    background: linear-gradient(135deg, var(--primary-dark), var(--secondary));
                }

                /* ===== BADGES ===== */
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .category-badge-charte {
                    display: inline-flex; align-items: center;
                    background: ${COLORS.primary}15;
                    color: var(--primary);
                    padding: 0.25rem 0.65rem;
                    border-radius: 50px;
                    font-weight: 600; font-size: 0.7rem;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.3rem 0.75rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                    white-space: nowrap;
                }
                .quantity-badge-charte {
                    display: inline-flex; align-items: center; justify-content: center;
                    min-width: 45px; padding: 0.25rem 0.6rem;
                    background: #f1f5f9; border-radius: 12px;
                    font-weight: 600; font-size: 0.8rem; color: #475569;
                }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }

                /* ===== TABS ===== */
                .tracabilite-tabs-charte {
                    border-bottom: 2px solid #f1f5f9;
                }
                .tracabilite-tabs-charte .nav-link {
                    border: none;
                    color: #64748b;
                    font-weight: 500;
                    padding: 0.75rem 1.25rem;
                    transition: all 0.2s ease;
                    background: transparent;
                    position: relative;
                }
                .tracabilite-tabs-charte .nav-link:hover {
                    color: var(--primary);
                    background: rgba(33, 172, 205, 0.05);
                }
                .tracabilite-tabs-charte .nav-link.active {
                    color: var(--primary) !important;
                    background: transparent;
                }
                .tracabilite-tabs-charte .nav-link.active::after {
                    content: '';
                    position: absolute;
                    bottom: -2px;
                    left: 1.25rem;
                    right: 1.25rem;
                    height: 2px;
                    background: var(--primary);
                    border-radius: 2px;
                }
                .badge-tab-count-charte {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 22px;
                    height: 22px;
                    padding: 0 6px;
                    margin-left: 8px;
                    border-radius: 50px;
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    color: white;
                    font-size: 0.7rem;
                    font-weight: 700;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-link-charte {
                    color: var(--primary);
                    text-decoration: none;
                    font-weight: 600;
                    font-size: 0.85rem;
                }
                .table-link-charte:hover { text-decoration: underline; }

                /* ===== CHRONOLOGY ===== */
                .chronology-list-charte { position: relative; }
                .chronology-item-charte {
                    display: flex;
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                    padding-left: 1rem;
                    border-left: 2px solid ${COLORS.primary}30;
                    position: relative;
                }
                .chronology-item-charte:last-child { margin-bottom: 0; }
                .chronology-icon-charte {
                    width: 40px; height: 40px;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                }
                .chronology-icon-charte i { font-size: 1.1rem; }
                .chronology-content-charte { flex: 1; }

                /* ===== LOT CARD ===== */
                .lot-card-charte {
                    transition: all 0.3s ease;
                }
                .lot-card-charte:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 30px rgba(0,0,0,0.08) !important;
                }
                .info-row-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 0.6rem 0;
                    border-bottom: 1px dashed #e2e8f0;
                }
                .info-row-charte:last-child { border-bottom: none; }
                .info-label-charte { color: #64748b; font-size: 0.8rem; }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled { opacity: 0.6; transform: none; }
                .btn-toggle-filters-charte { font-size: 0.85rem; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                    .product-header-charte { padding: 1rem; flex-wrap: wrap; }
                    .tracabilite-tabs-charte .nav-link { padding: 0.75rem 0.75rem; font-size: 0.8rem; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}